import { WorkOrderWorkStepFlowStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import ClipboardIcon from "@redotech/redo-icons/clipboard-check_filled.svg";
import { RedoOutboundLabelsRpcClientContext } from "@redotech/redo-merchant-fulfillment-app/outbound-labels-rpc-client";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { Handle, Position } from "@xyflow/react";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../../steps";
import { BaseNode } from "../base-node";

export interface WorkOrderWorkStepNodeData {
  step: PartialWithRequiredType<WorkOrderWorkStepFlowStep>;
  deleteElements: (id: string) => void;
  hovered?: boolean;
}

export const WorkOrderWorkStepNode = memo(function WorkOrderWorkStepNode({
  id,
  data,
  selected,
}: {
  id: string;
  data: WorkOrderWorkStepNodeData;
  selected?: boolean;
}) {
  const { t } = useTranslation();
  const { data: stationsData } = useRpcQuery(
    RedoOutboundLabelsRpcClientContext,
    { method: "getWorkOrderStations" as const, args: {} },
  );

  const stationName = useMemo(() => {
    if (!data.step.stationId || !stationsData?.stations) {
      return null;
    }
    return (
      stationsData.stations.find((s) => s.id === data.step.stationId)?.name ??
      null
    );
  }, [data.step.stationId, stationsData]);

  return (
    <>
      <Handle position={Position.Top} type="target" />
      <BaseNode
        hovered={data.hovered}
        icon={
          <RedoFeaturedIcon
            color="success"
            Icon={ClipboardIcon}
            shape="circle"
            size="md"
          />
        }
        onDelete={() => data.deleteElements(id)}
        selected={selected}
        step={data.step}
        title={t("Work step")}
      >
        <Flex dir="column" gap="xs" w="full">
          {data.step.title ? (
            <Text fontSize="xs" fontWeight="semibold">
              {data.step.title}
            </Text>
          ) : (
            <Text fontSize="xs" textColor="tertiary">
              {t("No title set")}
            </Text>
          )}
          {stationName && (
            <Text fontSize="xxs" textColor="tertiary">
              {t("Station: {{stationName}}", { stationName })}
            </Text>
          )}
        </Flex>
      </BaseNode>
      <Handle position={Position.Bottom} type="source" />
    </>
  );
});
